import { useEffect, useState, useCallback } from "react";
import { opApi, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { HelpTip } from "@/components/mc/HelpTip";
import { CartTypeChooser } from "@/components/mc/CartTypeChooser";
import { AdminOnlyTag, useCartControls } from "@/components/mc/CartControls";
import { BookingSettings } from "@/components/mc/BookingSettings";
import { DayColumns } from "@/components/mc/appt/ApptCard";
import { ApptSearch } from "@/components/mc/appt/ApptSearch";
import { WalkInButton } from "@/components/mc/appt/WalkIn";
import { ActivityLog } from "@/components/mc/appt/ActivityLog";

const VIEWER = { manage: true, providerId: null, shared: true };

const DAYS = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];
const money = (n) => "$" + Number(n || 0).toFixed(2);
const emptyHours = () => DAYS.reduce((a, [k]) => { a[k] = { off: k === "sun", open: "09:00", close: "17:00" }; return a; }, {});

export default function BarberAdmin({ onCartChanged }) {
  const cartControls = useCartControls();
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [appts, setAppts] = useState([]);
  const [apptDate, setApptDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [svcDlg, setSvcDlg] = useState(null);
  const [provDlg, setProvDlg] = useState(null);
  const [sections, setSections] = useState(["services", "providers", "appointments"]);

  const loadServices = useCallback(() => opApi.get("/tenant/services").then(({ data }) => setServices(data || [])).catch(() => {}), []);
  const loadProviders = useCallback(() => opApi.get("/tenant/providers").then(({ data }) => setProviders(data || [])).catch(() => {}), []);
  const loadAppts = useCallback((d) => opApi.get("/tenant/appointments", { params: { date: d } }).then(({ data }) => setAppts(data || [])).catch(() => {}), []);
  useEffect(() => { loadServices(); loadProviders(); }, [loadServices, loadProviders]);
  useEffect(() => { loadAppts(apptDate); }, [apptDate, loadAppts]);

  const saveService = () => {
    const b = { name: svcDlg.name?.trim(), price: Number(svcDlg.price) || 0, duration_min: Number(svcDlg.duration_min) || 30, deposit: Number(svcDlg.deposit) || 0, description: svcDlg.description || "", active: svcDlg.active !== false, sort_order: Number(svcDlg.sort_order) || 0 };
    if (!b.name) { toast.error("Name is required"); return; }
    const req = svcDlg.id ? opApi.put(`/tenant/services/${svcDlg.id}`, b) : opApi.post("/tenant/services", b);
    req.then(() => { toast.success("Service saved"); setSvcDlg(null); loadServices(); }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  };
  const delService = (id) => opApi.delete(`/tenant/services/${id}`).then(() => { toast.success("Removed"); loadServices(); }).catch(() => {});

  const saveProvider = () => {
    const b = { name: provDlg.name?.trim(), bio: provDlg.bio || "", avatar: provDlg.avatar || "", active: provDlg.active !== false, sort_order: Number(provDlg.sort_order) || 0, hours: provDlg.hours || emptyHours() };
    if (!b.name) { toast.error("Name is required"); return; }
    const req = provDlg.id ? opApi.put(`/tenant/providers/${provDlg.id}`, b) : opApi.post("/tenant/providers", b);
    req.then(() => { toast.success("Provider saved"); setProvDlg(null); loadProviders(); }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  };
  const delProvider = (id) => opApi.delete(`/tenant/providers/${id}`).then(() => { toast.success("Removed"); loadProviders(); }).catch(() => {});

  const setStatus = (id, status) => opApi.patch(`/tenant/appointments/${id}`, { status }).then(() => { toast.success("Updated"); loadAppts(apptDate); }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  const setDeposit = (id, deposit_status) => opApi.patch(`/tenant/appointments/${id}/deposit`, { deposit_status }).then(() => { toast.success("Deposit updated"); loadAppts(apptDate); }).catch(() => {});

  const setDay = (day, patch) => setProvDlg((p) => ({ ...p, hours: { ...(p.hours || emptyHours()), [day]: { ...(p.hours?.[day] || { off: false, open: "09:00", close: "17:00" }), ...patch } } }));

  return (
    <div className="max-w-4xl">
      {cartControls !== "hidden" && (
      <div className="mb-6">
        <h2 className="text-sm font-semibold uppercase tracking-wider mb-3 flex items-center">Cart type<HelpTip testid="help-carttype" text="Choose the industry template that matches your business. Switching is safe — nothing is deleted." />{cartControls === "admin" && <AdminOnlyTag />}</h2>
        <CartTypeChooser onChanged={onCartChanged} />
      </div>
      )}

      <Accordion type="multiple" value={sections} onValueChange={setSections} className="border border-border">
        {/* SERVICES */}
        <AccordionItem value="services" className="px-6">
          <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="barber-section-services">Services</AccordionTrigger>
          <AccordionContent className="pb-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-muted-foreground flex items-center">What customers can book<HelpTip testid="help-services" text="Each service has a name, price and a length in minutes. The length decides how time slots are generated." /></p>
              <Button size="sm" className="rounded-none gap-1" onClick={() => setSvcDlg({ active: true, duration_min: 30, price: 0, deposit: 0 })} data-testid="add-service-btn"><Plus className="h-4 w-4" />Add service</Button>
            </div>
            {services.length === 0 && <div className="text-sm text-muted-foreground border border-dashed p-6 text-center" data-testid="services-empty">No services yet. Add your first — e.g. "Haircut", 30 min, $30.</div>}
            <div className="space-y-2">
              {services.map((s) => (
                <div key={s.id} className="flex items-center justify-between border border-border p-3" data-testid={`service-row-${s.id}`}>
                  <div><div className="font-medium text-sm">{s.name}</div><div className="text-xs text-muted-foreground">{s.duration_min} min · {money(s.price)}{s.deposit > 0 ? ` · ${money(s.deposit)} deposit` : ""}</div></div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="rounded-none h-8 w-8" onClick={() => setSvcDlg(s)} data-testid={`edit-service-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="rounded-none h-8 w-8 text-destructive" onClick={() => delService(s.id)} data-testid={`del-service-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* BOOKING SETTINGS */}
        <AccordionItem value="settings" className="px-6">
          <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="barber-section-settings">Deposits, cancellations &amp; reminders</AccordionTrigger>
          <AccordionContent className="pb-6"><BookingSettings /></AccordionContent>
        </AccordionItem>

        {/* PROVIDERS */}
        <AccordionItem value="providers" className="px-6">
          <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="barber-section-providers">Providers</AccordionTrigger>
          <AccordionContent className="pb-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-muted-foreground flex items-center">Your bookable team<HelpTip testid="help-providers" text="Each provider has their own working days and hours. Customers can pick a provider or 'no preference'." /></p>
              <Button size="sm" className="rounded-none gap-1" onClick={() => setProvDlg({ active: true, hours: emptyHours() })} data-testid="add-provider-btn"><Plus className="h-4 w-4" />Add provider</Button>
            </div>
            {providers.length === 0 && <div className="text-sm text-muted-foreground border border-dashed p-6 text-center" data-testid="providers-empty">No providers yet. Add the people who take appointments.</div>}
            <div className="space-y-2">
              {providers.map((p) => (
                <div key={p.id} className="flex items-center justify-between border border-border p-3" data-testid={`provider-row-${p.id}`}>
                  <div><div className="font-medium text-sm">{p.name}</div><div className="text-xs text-muted-foreground">{DAYS.filter(([k]) => !(p.hours?.[k]?.off)).map(([, l]) => l).join(", ") || "No days set"}</div></div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="rounded-none h-8 w-8" onClick={() => setProvDlg({ ...p, hours: p.hours || emptyHours() })} data-testid={`edit-provider-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="rounded-none h-8 w-8 text-destructive" onClick={() => delProvider(p.id)} data-testid={`del-provider-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* APPOINTMENTS (team view) */}
        <AccordionItem value="appointments" className="px-6">
          <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="barber-section-appointments">Appointments</AccordionTrigger>
          <AccordionContent className="pb-6">
            <div className="mb-4"><ApptSearch viewer={VIEWER} onStatus={setStatus} refreshKey={appts} /></div>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Label className="text-sm">Date</Label>
              <Input type="date" value={apptDate} onChange={(e) => setApptDate(e.target.value)} className="rounded-none w-auto" data-testid="appt-date" />
              <HelpTip testid="help-appointments" text="Combined team view for the day. Check in, confirm, complete or cancel each appointment. Every change is recorded in the Calendar activity log below." />
              <div className="flex-1" />
              <WalkInButton services={services} providers={providers} viewer={VIEWER} onAdded={() => loadAppts(apptDate)} />
            </div>
            <DayColumns providers={providers} appts={appts} viewer={VIEWER} onStatus={setStatus} onDeposit={setDeposit} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Accordion type="multiple" className="border border-t-0 border-border">
        <AccordionItem value="activity" className="px-6">
          <AccordionTrigger className="text-sm font-semibold uppercase tracking-wider" data-testid="barber-section-activity">Calendar activity log</AccordionTrigger>
          <AccordionContent className="pb-6"><ActivityLog providers={providers} /></AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Service popup card */}
      <Dialog open={!!svcDlg} onOpenChange={(o) => !o && setSvcDlg(null)}>
        <DialogContent className="rounded-none" data-testid="service-dialog">
          <DialogHeader><DialogTitle>{svcDlg?.id ? "Edit service" : "Add service"}</DialogTitle></DialogHeader>
          {svcDlg && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input className="rounded-none mt-1" value={svcDlg.name || ""} onChange={(e) => setSvcDlg({ ...svcDlg, name: e.target.value })} data-testid="service-name" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="flex items-center">Price<HelpTip text="What you charge for this service." /></Label><Input type="number" className="rounded-none mt-1" value={svcDlg.price ?? 0} onChange={(e) => setSvcDlg({ ...svcDlg, price: e.target.value })} data-testid="service-price" /></div>
                <div><Label className="flex items-center">Minutes<HelpTip text="How long it takes. Controls time-slot length." /></Label><Input type="number" className="rounded-none mt-1" value={svcDlg.duration_min ?? 30} onChange={(e) => setSvcDlg({ ...svcDlg, duration_min: e.target.value })} data-testid="service-duration" /></div>
                <div><Label>Deposit</Label><Input type="number" className="rounded-none mt-1" value={svcDlg.deposit ?? 0} onChange={(e) => setSvcDlg({ ...svcDlg, deposit: e.target.value })} data-testid="service-deposit" /></div>
              </div>
              <div><Label>Description</Label><Textarea className="rounded-none mt-1" value={svcDlg.description || ""} onChange={(e) => setSvcDlg({ ...svcDlg, description: e.target.value })} data-testid="service-desc" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" className="rounded-none" onClick={() => setSvcDlg(null)}>Cancel</Button><Button className="rounded-none" onClick={saveService} data-testid="save-service">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Provider popup card */}
      <Dialog open={!!provDlg} onOpenChange={(o) => !o && setProvDlg(null)}>
        <DialogContent className="rounded-none max-h-[90vh] overflow-y-auto" data-testid="provider-dialog">
          <DialogHeader><DialogTitle>{provDlg?.id ? "Edit provider" : "Add provider"}</DialogTitle></DialogHeader>
          {provDlg && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input className="rounded-none mt-1" value={provDlg.name || ""} onChange={(e) => setProvDlg({ ...provDlg, name: e.target.value })} data-testid="provider-name" /></div>
              <div><Label>Bio <span className="text-muted-foreground normal-case">(optional)</span></Label><Input className="rounded-none mt-1" value={provDlg.bio || ""} onChange={(e) => setProvDlg({ ...provDlg, bio: e.target.value })} data-testid="provider-bio" /></div>
              <div>
                <Label className="flex items-center">Working hours<HelpTip text="Turn a day on/off and set open & close times. Slots are generated only within these hours." /></Label>
                <div className="mt-2 space-y-1.5">
                  {DAYS.map(([k, lbl]) => {
                    const h = provDlg.hours?.[k] || { off: false, open: "09:00", close: "17:00" };
                    return (
                      <div key={k} className="flex items-center gap-2" data-testid={`provider-day-${k}`}>
                        <span className="w-10 text-sm">{lbl}</span>
                        <Switch checked={!h.off} onCheckedChange={(v) => setDay(k, { off: !v })} data-testid={`provider-day-toggle-${k}`} />
                        {!h.off ? (
                          <>
                            <Input type="time" value={h.open} onChange={(e) => setDay(k, { open: e.target.value })} className="rounded-none w-28" data-testid={`provider-open-${k}`} />
                            <span className="text-muted-foreground">–</span>
                            <Input type="time" value={h.close} onChange={(e) => setDay(k, { close: e.target.value })} className="rounded-none w-28" data-testid={`provider-close-${k}`} />
                          </>
                        ) : <span className="text-sm text-muted-foreground">Off</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" className="rounded-none" onClick={() => setProvDlg(null)}>Cancel</Button><Button className="rounded-none" onClick={saveProvider} data-testid="save-provider">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
